// src/pages/Tarifs/components/PricingApplicationSelector.jsx

import React from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa";

import {
  PRICING_CATEGORIES,
} from "../data/pricingCatalog";

import {
  cn,
  getInitials,
} from "../utils/pricingUtils";

function ApplicationLogo({ application }) {
  const Icon = application.icon;

  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[16px] border border-slate-200 bg-white dark:border-white/10 dark:bg-white/5">
      <span className="absolute text-xs font-semibold text-slate-300 dark:text-slate-600">
        {getInitials(application.name)}
      </span>

      {application.logo ? (
        <img
          src={application.logo}
          alt=""
          loading="lazy"
          className="relative z-10 h-full w-full object-contain p-2"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : Icon ? (
        <Icon className="relative z-10 text-xl text-[#0868f2]" />
      ) : null}
    </div>
  );
}

function CategoryNavigation({
  activeCategory,
  onCategoryChange,
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div className="flex min-w-max border-b border-slate-200 dark:border-white/10">
        {PRICING_CATEGORIES.map((category) => {
          const active = category.id === activeCategory;

          return (
            <button
              key={category.id}
              type="button"
              onClick={() => onCategoryChange(category.id)}
              className={cn(
                "group relative min-w-[180px] px-5 pb-5 pt-2 text-left",
                "transition-colors first:pl-0",
                active
                  ? "text-[#0868f2] dark:text-blue-300"
                  : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
              )}
            >
              <span className="block text-sm font-semibold">
                {category.label}
              </span>

              <span className="mt-1 block max-w-[210px] text-xs leading-5 opacity-75">
                {category.description}
              </span>

              <span
                className={cn(
                  "absolute inset-x-5 -bottom-px h-[2px] bg-[#ff7900]",
                  "origin-left transition-transform duration-300 first:left-0",
                  active
                    ? "scale-x-100"
                    : "scale-x-0 group-hover:scale-x-100",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApplicationChoice({
  application,
  selected,
  onSelect,
}) {
  const Icon = application.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(application.id)}
      className={cn(
        "group relative w-full overflow-hidden rounded-[20px] border p-5 text-left",
        "transition-all duration-300",
        selected
          ? [
              "border-[#0868f2] bg-blue-50/60",
              "shadow-[0_18px_45px_-32px_rgba(8,104,242,0.65)]",
              "dark:border-blue-400 dark:bg-blue-500/10",
            ]
          : [
              "border-slate-200 bg-white",
              "hover:-translate-y-0.5 hover:border-[#0868f2]/35",
              "dark:border-white/10 dark:bg-slate-900",
              "dark:hover:border-blue-400/30",
            ],
      )}
      aria-pressed={selected}
    >
      {selected ? (
        <span className="absolute right-4 top-4 grid h-7 w-7 place-items-center rounded-full bg-[#0868f2] text-white">
          <FaCheck className="text-[9px]" />
        </span>
      ) : null}

      <div className="flex items-start gap-4 pr-8">
        <ApplicationLogo application={application} />

        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.17em] text-[#ff7900]">
            {application.badge}
          </p>

          <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-950 transition-colors group-hover:text-[#0868f2] dark:text-white dark:group-hover:text-blue-300">
            {application.name}
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {application.short}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-white/10">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {application.audience}
        </span>

        <FaArrowRight
          className={cn(
            "shrink-0 text-[10px] transition-all",
            selected
              ? "translate-x-1 text-[#ff7900]"
              : "text-slate-300 group-hover:translate-x-1 group-hover:text-[#ff7900]",
          )}
        />
      </div>
    </button>
  );
}

export default function PricingApplicationSelector({
  applications,
  activeCategory,
  selectedApplicationId,
  onCategoryChange,
  onApplicationSelect,
}) {
  return (
    <div>
      <CategoryNavigation
        activeCategory={activeCategory}
        onCategoryChange={onCategoryChange}
      />

      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {applications.map((application) => (
          <ApplicationChoice
            key={application.id}
            application={application}
            selected={selectedApplicationId === application.id}
            onSelect={onApplicationSelect}
          />
        ))}
      </div>
    </div>
  );
}